import { createElement } from "react";
import { createRoot } from "react-dom/client";
import { loadAppConfig, getConfigDefaults } from "../api/configLoader.js";
import { applyGlobalPrivacyControlDefaultIfNeeded } from "../shared/cookieConsent/cookieConsentStorage.js";
import { shouldShowMaintenanceScreen } from "../app/maintenanceGate.js";
import { isQuoteEmbedPathname, loadQuoteEmbedBootstrap } from "../embeds/loadQuoteEmbedBootstrap.js";
import { isLabelsEmbedPathname, loadLabelsEmbedBootstrap } from "../embeds/loadLabelsEmbedBootstrap.js";
import { NM_LABELS_EMBED_SDK_SOURCE } from "../embeds/labelsEmbedProtocol.js";
import { registerPackageDropdownToggle } from "../shared/utils/packageDropdownToggle.js";
import { syncDocumentNativeSelectChromeClass } from "../shared/utils/nmNativeSelectLightChrome.js";
import { markRootBooted } from "../shared/utils/markRootBooted.js";
registerPackageDropdownToggle();
syncDocumentNativeSelectChromeClass();
const CONFIG_WAIT_MS = 6000;

function waitForLabelsEmbedToken(timeoutMs = 60000) {
  return new Promise((resolve, reject) => {
    const params = new URLSearchParams(window.location.search);
    const fromQuery = (params.get("token") || params.get("t") || "").trim();
    if (fromQuery) {
      resolve(fromQuery);
      return;
    }
    let settled = false;
    const timer = setTimeout(() => {
      if (settled) return;
      settled = true;
      window.removeEventListener("message", onMsg);
      reject(new Error("Timed out waiting for embed token. Call nitromule.init() then purchaseLabels()."));
    }, timeoutMs);
    function onMsg(event) {
      const data = event.data;
      if (!data || data.source !== NM_LABELS_EMBED_SDK_SOURCE) return;
      if (data.type !== "INIT" || !data.payload?.token) return;
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      window.removeEventListener("message", onMsg);
      resolve(String(data.payload.token));
    }
    window.addEventListener("message", onMsg);
    // Signal parent we are listening (pre-bootstrap)
    try {
      if (window.parent && window.parent !== window) {
        window.parent.postMessage(
          { source: "nitromule-labels-embed", type: "APP_READY", payload: { awaiting_token: true } },
          "*"
        );
      }
    } catch (_) {}
  });
}

(async function bootstrap() {
  const root = document.getElementById('nitromule-react-root');
  if (!root) return;
  if (isLabelsEmbedPathname()) {
    document.documentElement.classList.add('nm-embed-labels-active');
    try {
      const token = await waitForLabelsEmbedToken();
      await loadLabelsEmbedBootstrap(token);
      const { mountLabelsEmbedTree } = await import("../embeds/LabelsEmbedRoot.jsx");
      const { default: AppErrorBoundary } = await import("../app/AppErrorBoundary");
      createRoot(root).render(createElement(AppErrorBoundary, null, mountLabelsEmbedTree()));
      markRootBooted(root);
    } catch (e) {
      console.error('[nm] labels embed bootstrap failed:', e);
      const code = e?.code === 'UNAUTHORIZED' ? 'UNAUTHORIZED' : 'UNKNOWN';
      try {
        if (window.parent && window.parent !== window) {
          window.parent.postMessage(
            {
              source: "nitromule-labels-embed",
              type: "ERROR",
              payload: { code, detail: e?.message || "Could not load labels embed" }
            },
            "*"
          );
        }
      } catch (_) {}
      root.innerHTML =
        '<p style="padding:16px;font-family:Noto Sans,system-ui,sans-serif;font-size:14px;color:#b00020;">Could not load Labels embed. Check the JWT or try again.</p>';
      markRootBooted(root);
    }
    return;
  }
  const embedPath = isQuoteEmbedPathname();
  if (embedPath) {
    document.documentElement.classList.add('nm-embed-quote-active');
    const params = new URLSearchParams(window.location.search);
    const k = (params.get('k') || '').trim();
    if (!k) {
      root.innerHTML = '<p style="padding:16px;font-family:Noto Sans,system-ui,sans-serif;font-size:14px;">Missing embed key. Use <code style="font-size:13px;">?k=your_key</code> in the iframe URL.</p>';
      markRootBooted(root);
      return;
    }
    try {
      await loadQuoteEmbedBootstrap(k);
    } catch (e) {
      console.error('[nm] quote embed bootstrap failed:', e);
      root.innerHTML = '<p style="padding:16px;font-family:Noto Sans,system-ui,sans-serif;font-size:14px;color:#b00020;">Could not load quote embed. Check the key or try again later.</p>';
      markRootBooted(root);
      return;
    }
    try {
      const {
        mountQuoteEmbedTree
      } = await import("../embeds/QuoteEmbedRoot.jsx");
      const {
        default: AppErrorBoundary
      } = await import("../app/AppErrorBoundary");
      createRoot(root).render(createElement(AppErrorBoundary, null, mountQuoteEmbedTree()));
      markRootBooted(root);
    } catch (err) {
      console.error('[nm] quote embed mount failed:', err);
      root.innerHTML = '<p style="padding:16px;font-family:Noto Sans,system-ui,sans-serif;">Something went wrong loading the quote widget.</p>';
      markRootBooted(root);
    }
    return;
  }
  try {
    const { consumeAdminAccessCodeIfPresent } = await import("../api/adminAccessHandoff.js");
    await consumeAdminAccessCodeIfPresent();
  } catch (e) {
    console.error('[nm] admin access handoff failed:', e?.message || e);
    try {
      window.alert(e?.message || 'Admin access link expired. Try again from Admin → Users.');
    } catch (_) {}
  }
  try {
    const configPromise = loadAppConfig();
    const timeoutPromise = new Promise(resolve => setTimeout(() => resolve('timeout'), CONFIG_WAIT_MS));
    const raceResult = await Promise.race([configPromise, timeoutPromise]);
    if (raceResult === 'timeout') {
      await configPromise.catch(() => {});
    }
    if (!window.nitromuleReactData) {
      window.nitromuleReactData = {
      ...getConfigDefaults(),
        apiUrl: `${window.location.origin}/api`
      };
    }
  } catch (e) {
    console.error('NitroMule config load failed:', e);
    window.nitromuleReactData = window.nitromuleReactData || {
      ...getConfigDefaults(),
      apiUrl: `${window.location.origin}/api`
    };
  }
  const data = window.nitromuleReactData || {};
  if (!data.portalConfig && root) {
    root.setAttribute("data-nm-main-site", "1");
  }
  applyGlobalPrivacyControlDefaultIfNeeded();
  let forceResolvedTheme = null;
  let flatSurface = null;
  try {
    const { portalForcedResolvedTheme, portalFlatSurfaceMode } = await import("@shared/portalColorSchemeMode.js");
    const mode = data.portalConfig?.color_scheme_mode;
    forceResolvedTheme = portalForcedResolvedTheme(mode);
    flatSurface = portalFlatSurfaceMode(mode);
    if (forceResolvedTheme) {
      document.documentElement.setAttribute("data-nm-resolved", forceResolvedTheme);
      if (flatSurface) document.documentElement.setAttribute("data-nm-flat-surface", flatSurface);
      else document.documentElement.removeAttribute("data-nm-flat-surface");
      const rootEl = document.getElementById("nitromule-react-root");
      if (rootEl) {
        rootEl.setAttribute("data-theme", forceResolvedTheme);
        if (flatSurface) rootEl.setAttribute("data-nm-flat-surface", flatSurface);
        else rootEl.removeAttribute("data-nm-flat-surface");
      }
    }
  } catch (_) {}
  try {
    const { mountScaleToZeroWakeUp } = await import("./mountScaleToZeroWakeUp.js");
    await mountScaleToZeroWakeUp();
  } catch (e) {
    console.error("[nm] scale-to-zero wake mount failed:", e?.message || e);
  }
  // Marketing paths: server serves marketing.html; if index.html SPA loads anyway, hand off.
  if (!data.portalConfig) {
    const { shouldMainSiteSpaDeferToMarketing } = await import("./marketingDocumentRedirect.js");
    if (
      shouldMainSiteSpaDeferToMarketing({
        pathname: window.location.pathname
      })
    ) {
      const handoffKey = 'nm_mkt_spa_handoff';
      const pathKey = `${window.location.pathname}${window.location.search}`;
      let alreadyTried = false;
      try {
        alreadyTried = sessionStorage.getItem(handoffKey) === pathKey;
      } catch (_) {}
      if (!alreadyTried) {
        try {
          sessionStorage.setItem(handoffKey, pathKey);
        } catch (_) {}
        try {
          const { isLoggedOutLocally, logoutCurrentSession } = await import('../api/client.js');
          if (isLoggedOutLocally()) {
            await logoutCurrentSession();
          }
        } catch (_) {}
        window.location.replace(`${window.location.pathname}${window.location.search}${window.location.hash}`);
        return;
      }
      try {
        sessionStorage.removeItem(handoffKey);
      } catch (_) {}
    }
  }
  const shippingFormContainers = document.querySelectorAll(".shipping-form-react-container");
  shippingFormContainers.forEach(async container => {
    const {
      default: ShippingForm
    } = await import("../shipping/components/shippingForm");
    const {
      ThemedWidgetRoot
    } = await import("../shared/components/ThemedWidgetRoot.jsx");
    createRoot(container).render(createElement(ThemedWidgetRoot, null, createElement(ShippingForm)));
  });
  const AppContainers = document.querySelectorAll("#nitromule-react-root");
  AppContainers.forEach(async container => {
    try {
      const data = window.nitromuleReactData || {};
      if (await shouldShowMaintenanceScreen(data)) {
        const {
          default: MaintenanceScreen
        } = await import("../app/MaintenanceScreen");
        const {
          ThemedWidgetRoot
        } = await import("../shared/components/ThemedWidgetRoot.jsx");
        createRoot(container).render(createElement(ThemedWidgetRoot, null, createElement(MaintenanceScreen)));
        markRootBooted(container);
        return;
      }
      const isPortalMode = !!data.portalConfig;
      const {
        default: App
      } = isPortalMode
        ? await import("../workspace/portal/PortalApp.jsx")
        : await import("../app/App");
      const {
        ThemeProvider
      } = await import("../shared/context/ThemeContext");
      const {
        default: AppErrorBoundary
      } = await import("../app/AppErrorBoundary");
      const tree = createElement(ThemeProvider, {
        ...(forceResolvedTheme ? {
          forceResolvedTheme
        } : {}),
        ...(flatSurface ? {
          flatSurface
        } : {})
      }, createElement(AppErrorBoundary, null, createElement(App)));
      createRoot(container).render(tree);
      markRootBooted(container);
    } catch (err) {
      console.error('NitroMule app mount failed:', err);
      container.innerHTML = '<div style="padding: 24px; font-family: sans-serif;"><p style="color: var(--nm-red);">Something went wrong loading the app.</p><p style="color: var(--nm-neutral-tertiary-content);">Check the browser console for details.</p></div>';
      markRootBooted(container);
    }
  });
  const trackingContainers = document.querySelectorAll(".tracking-react-container");
  trackingContainers.forEach(async container => {
    const {
      default: Tracking
    } = await import("../tracking/Tracking");
    const {
      ThemedWidgetRoot
    } = await import("../shared/components/ThemedWidgetRoot.jsx");
    createRoot(container).render(createElement(ThemedWidgetRoot, null, createElement(Tracking)));
  });
  const faqsContainers = document.querySelectorAll(".faqs-react-container");
  faqsContainers.forEach(async container => {
    const category = container.dataset.category || '';
    const {
      default: FAQs
    } = await import("../faqs/FAQs");
    const {
      ThemedWidgetRoot
    } = await import("../shared/components/ThemedWidgetRoot.jsx");
    createRoot(container).render(createElement(ThemedWidgetRoot, null, createElement(FAQs, {
      category: category
    })));
  });
})();
